<template>
  <!-- Diseño para móvil -->
  <MobileAuthTemplate
    v-if="isMobile"
    ref="mobileAuthRef"
    :form-data="{
      email: authData.Usuario,
      password: authData.Password,
      mantenerSesion: authData.mantenerSesion,
    }"
    :form-rules="{
      email: authRules.Usuario,
      password: authRules.Password,
    }"
    :loading="isLoading"
    :logo-url="Isologotipo"
    email-placeholder="Usuario"
    password-placeholder="Contraseña"
    button-text="Ingresar"
    :client-name="mostrarNombreClienteLogin ? identidadPublica.nombreCliente : ''"
    :version="version"
    @submit="handleMobileLogin"
    @update:form-data="handleFormDataUpdate"
  />

  <!-- Diseño para desktop (actual) -->
  <div
    v-else
    class="desktop-login-container"
  >
    <div class="logo-container">
      <img
        :src="Isologotipo"
        alt="Logo"
        style="max-width: 300px"
      />
      <span style="font-size: 12px; color: white; align-self: flex-end">{{ version }}</span>
      <span
        v-if="mostrarNombreClienteLogin"
        class="login-client-name"
      >
        {{ identidadPublica.nombreCliente }}
      </span>
    </div>
    <div class="form-container">
      <el-form
        ref="formRef"
        :model="authData"
        :rules="authRules"
        label-width="100px"
      >
        <el-form-item
          prop="Usuario"
          label="Usuario"
        >
          <el-input
            v-model="authData.Usuario"
            placeholder="Introduce tu usuario"
            size="default"
            class="full-width"
            clearable
            @keydown.enter="login"
          ></el-input>
        </el-form-item>

        <el-form-item
          prop="Password"
          label="Contraseña"
        >
          <el-input
            v-model="authData.Password"
            placeholder="Introduce tu contraseña"
            size="default"
            show-password
            class="full-width"
            @keydown.enter="login"
          ></el-input>
        </el-form-item>

        <el-form-item>
          <div class="button-container">
            <el-button
              type="primary"
              size="default"
              :disabled="authDisabled"
              @click="login"
              >Ingresar</el-button
            >
          </div>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script setup>
import {
  reactive,
  ref,
  computed,
  onMounted,
} from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';

import Isologotipo from '@/assets/images/isologotipo.png';
import { useAuthStore } from '@/views/auth/auth_store';
import { useMessageError } from '@/shared/composables/message_error.js';
import { useLoading } from '@/shared/composables/loading.js';
import { useDeviceDetection } from '@/shared/composables/use_device_detection.js';
import { useConfiguracionStore } from '@/stores/configuracion-general.js';
import MobileAuthTemplate from './components/MobileAuthTemplate.vue';
import { Role } from '@/enum/roles_enum.js';

const configuracionStore = useConfiguracionStore();
const { identidadPublica } = storeToRefs(configuracionStore);

const mostrarNombreClienteLogin = computed(() => {
  return identidadPublica.value.visible && !!identidadPublica.value.nombreCliente?.trim();
});
// Cargar fuente Inter para mobile y limpiar campos al cargar
onMounted(async () => {
  if (!document.querySelector('link[href*="Inter"]')) {
    const link = document.createElement('link');
    link.href =
      'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }
  await configuracionStore.findIdentidadPublicaHTTP();
  // Limpiar campos al cargar la página de login
  clearFormData();
});

const formRef = ref();
const mobileAuthRef = ref();
const isLoading = ref(false);
const authStore = useAuthStore();
const { authData } = storeToRefs(authStore);
const { isMobile } = useDeviceDetection();
const version = __APP_VERSION__;

const router = useRouter();

const authRules = reactive({
  Usuario: [
    { required: true, min: 4, message: 'Por favor, introduce tu usuario', trigger: 'blur' },
  ],
  Password: [
    { required: true, min: 4, message: 'Por favor, introduce tu contraseña', trigger: 'blur' },
  ],
});

const authDisabled = computed(() => {
  return !(authData.value.Usuario.length >= 4 && authData.value.Password.length >= 4);
});

// Función para determinar la ruta de redirección basada en los roles del usuario
const getRedirectRoute = (usuario) => {
  if (!usuario || !usuario.roles || usuario.roles.length === 0) {
    return '/dashboard';
  }

  const rolesUsuario = usuario.roles.map((r) => r.Codigo);

  // Prioridad de redirección según roles
  // 1. ADMINISTRADOR -> Dashboard (máxima prioridad)
  if (rolesUsuario.includes(Role.ADMINISTRADOR)) {
    return '/dashboard';
  }

  // 2. PLANEACION -> Movimientos WIP (Pallet)
  if (rolesUsuario.includes(Role.PLANEACION)) {
    return '/page/pallet/list';
  }

  // 3. EJECUTIVO_DE_VENTAS -> Propuestas
  if (rolesUsuario.includes(Role.EJECUTIVO_DE_VENTAS)) {
    return '/page/proposals/list';
  }

  // 4. PRESUPUESTISTA -> Solicitudes
  if (rolesUsuario.includes(Role.PRESUPUESTISTA)) {
    return '/page/requests/list';
  }

  // Por defecto -> Dashboard
  return '/dashboard';
};

const login = () => {
  formRef.value.validate(async (valid) => {
    if (valid) {
      try {
        isLoading.value = true;
        useLoading().openLoading();
        await authStore.authHttp();
        const usuario = authStore.usuario();
        const redirectRoute = getRedirectRoute(usuario);
        await router.replace(redirectRoute);
      } catch (err) {
        let message = 'Error al iniciar sesión';
        console.error(err);
        if (err?.response?.data?.errorCode && +err.response.data.errorCode === 102) {
          message = err.response.data.errorMessage;
        }
        useMessageError().open({ message });
      } finally {
        useLoading().closeLoading();
        isLoading.value = false;
      }
    } else {
      useMessageError().open({ message: 'Debe completar usuario y contraseña.' });
    }
  });
};

const handleMobileLogin = async (formData) => {
  // Actualizar datos del formulario principal
  authData.value.Usuario = formData.email;
  authData.value.Password = formData.password;
  authData.value.mantenerSesion = !!formData.mantenerSesion;

  // Ejecutar validación y login
  if (mobileAuthRef.value?.formRef) {
    const valid = await mobileAuthRef.value.formRef.validate().catch(() => false);
    if (valid) {
      try {
        isLoading.value = true;
        useLoading().openLoading();
        await authStore.authHttp();
        const usuario = authStore.usuario();
        const redirectRoute = getRedirectRoute(usuario);
        await router.replace(redirectRoute);
      } catch (err) {
        let message = 'Error al iniciar sesión';
        console.error(err);
        if (err?.response?.data?.errorCode && +err.response.data.errorCode === 102) {
          message = err.response.data.errorMessage;
        }
        useMessageError().open({ message });
      } finally {
        useLoading().closeLoading();
        isLoading.value = false;
      }
    } else {
      useMessageError().open({ message: 'Debe completar usuario y contraseña.' });
    }
  }
};

const handleFormDataUpdate = (formData) => {
  // Actualizar los datos principales cuando el usuario escribe en el componente móvil
  authData.value.Usuario = formData.email;
  authData.value.Password = formData.password;
  authData.value.mantenerSesion = !!formData.mantenerSesion;
};

const clearFormData = () => {
  // Limpiar datos del store
  authData.value.Usuario = '';
  authData.value.Password = '';
  authData.value.mantenerSesion = false;

  // Limpiar validaciones del formulario desktop
  if (formRef.value) {
    formRef.value.clearValidate();
  }

  // Limpiar formulario móvil si existe
  if (mobileAuthRef.value && mobileAuthRef.value.clearForm) {
    mobileAuthRef.value.clearForm();
  }
};
</script>

<style scoped>
/* Estilos para Desktop (mantener los originales) */
.desktop-login-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
}
.login-client-name {
  margin-top: 8px;
  color: white;
  font-size: 15px;
  font-weight: 700;
  text-align: center;
  max-width: 300px;
  line-height: 1.2;
  word-break: break-word;
}

.logo-container {
  margin-bottom: 20px;
  background-color: #409eff;
  border-radius: 20px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.form-container {
  width: 400px;
  border: 1px solid #ebeef5;
  padding: 40px 40px 0px;
  border-radius: 5px;
}

.el-input__inner,
.el-button {
  font-size: 14px;
}

.el-form-item__label {
  font-size: 16px;
}

.button-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.el-button--primary {
  background-color: var(el-button--primary);
  border-color: var(el-button--primary);
}

.el-button--primary:hover,
.el-button--primary:focus {
  background-color: #66b1ff;
  border-color: #66b1ff;
}

.full-width {
  width: 100%;
}

/* Componente móvil extraído a MobileAuthTemplate.vue */
</style>
